import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useLanguage } from '../../i18n';
import styles from './ResourceCollections.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ... imports and rest unchanged ...

// Frontend mapping for icons/colors (based on slug)
const getCategoryStyle = (slug) => {
  const map = {
    'digital-fundamentals': {
      icon: 'lucide:shield',
      color: 'var(--color-info-light)'
    },
    'legal-aid': {
      icon: 'lucide:scale',
      color: 'var(--color-success-light)'
    },
    'mental-health': {
      icon: 'lucide:heart-pulse',
      color: 'var(--color-warning-light)'
    },
    'emergency-protocols': {
      icon: 'lucide:alert-triangle',
      color: 'var(--color-error-light)'
    },
    'regional': {
      icon: 'lucide:users',
      color: '#e9d5ff'
    },
    'policy-templates': {
      icon: 'lucide:file-text',
      color: '#fed7aa'
    }
    // Add more slugs as you create them in DB
  };

  return map[slug] || {
    icon: 'lucide:folder',
    color: '#e2e8f0' // fallback
  };
};

export default function ResourceCollections() {
  const { t } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const res = await fetch(`${API_URL}/collections`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCollections(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  const featured = collections.find(c => c.isFeatured) || collections[0] || null;

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Icon icon="lucide:loader-2" className={styles.spinner} />
          {t('loadingCollections') || 'Loading collections...'}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            <Icon icon="lucide:alert-triangle" />
          {t('errorLoadingCollections', { error })}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* ... header unchanged ... */}

        {/* Featured Collection */}
        {featured && (
          <section className={styles.featuredSection}>
            <div className={styles.featuredCard}>
              <div className={styles.featuredImagePlaceholder}></div>

              <div className={styles.featuredContent}>
                <div className={styles.featuredTags}>
                  {featured.tags?.map((tag, i) => (
                    <span key={i} className={styles.tag}>{tag}</span>
                  ))}
                </div>

                <h2 className={styles.featuredTitle}>{featured.title}</h2>
                <p className={styles.featuredDescription}>{featured.description}</p>

                <div className={styles.featuredMeta}>
                  <span>{featured.resources?.length || 0} {t('resources')}</span>
                  <span>{featured.rating || 'No ratings yet'}</span>
                </div>

                <Link to={`/collections/${featured.slug}`} className={styles.featuredButton}>
                  <Icon icon="lucide:folder-open" />
                  {t('openCollection')}
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* All Collections Grid */}
        <section className={styles.gridSection}>
          <h3 className={styles.gridTitle}>{t('allCollections')}</h3>

          <div className={styles.collectionsGrid}>
            {collections.map((col) => {
              const { icon, color } = getCategoryStyle(col.slug);
              return (
                <Link to={`/collections/${col.slug}`} key={col.id} className={styles.collectionCard}>
                  <div className={styles.cardIcon} style={{ backgroundColor: color }}>
                    <Icon icon={icon} />
                  </div>

                  <h4 className={styles.cardTitle}>{col.title}</h4>
                  <p className={styles.cardDescription}>{col.description}</p>

                  <div className={styles.cardFooter}>
                    <span className={styles.cardCount}>
                      {col.resources?.length || 0} {t('resources')}
                    </span>
                    <span className={styles.cardUpdated}>
                      Updated {new Date(col.updatedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </Link>
              );
            })}

            {collections.length === 0 && (
                          <p className={styles.emptyText}>{t('noCollectionsAvailable')}</p>
            )}
          </div>
        </section>

        {/* Load More */}
        <div className={styles.loadMore}>
          <button className={styles.loadMoreBtn}>
                      {t('loadMoreCollections')}
            <Icon icon="lucide:arrow-down" />
          </button>
          <p className={styles.paginationInfo}>
                      {t('showingCollections', { count: collections.length })}
          </p>
        </div>
      </div>
    </main>
  );
}
// import { Link } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './ResourceCollections.module.css';

// // Dummy data – in real app from API/state
// const dummyFeatured = {
//   title: "Essential Safeguarding Toolkit 2026",
//   description: "The most comprehensive bundle — digital security basics, emergency contacts, legal templates, mental health resources, updated for current threats.",
//   count: 248,
//   rating: "4.9 (1,204 ratings)",
//   tags: ["Comprehensive", "Multilingual", "Updated 2026"],
//   link: "/collections/essential-toolkit"
// };

// const dummyCollections = [
//   {
//     id: 1,
//     title: "Digital Security Fundamentals",
//     description: "Device protection, secure communication, threat modeling, best practices for high-risk users.",
//     count: 42,
//     updated: "Feb 2026",
//     tags: ["Digital", "High-Risk", "Beginner-Friendly"],
//     icon: "lucide:shield",
//     color: "var(--color-info-light)",
//     link: "/collections/digital-fundamentals"
//   },
//   {
//     id: 2,
//     title: "Legal Aid & Advocacy Tools",
//     description: "Emergency legal contacts, affidavit templates, rights documentation, advocacy guides.",
//     count: 31,
//     updated: "Jan 2026",
//     tags: ["Legal", "Advocacy", "Regional"],
//     icon: "lucide:scale",
//     color: "var(--color-success-light)",
//     link: "/collections/legal-aid"
//   },
//   {
//     id: 3,
//     title: "Mental Health & Resilience",
//     description: "Trauma-informed resources, crisis hotlines, peer support, self-care guides.",
//     count: 28,
//     updated: "Mar 2026",
//     tags: ["Mental Health", "Crisis", "Peer Support"],
//     icon: "lucide:heart-pulse",
//     color: "var(--color-warning-light)",
//     link: "/collections/mental-health"
//   },
//   {
//     id: 4,
//     title: "Emergency Response Protocols",
//     description: "Rapid response plans, evacuation checklists, incident reporting, crisis communication.",
//     count: 19,
//     updated: "Feb 2026",
//     tags: ["Emergency", "High-Priority", "Protocols"],
//     icon: "lucide:alert-triangle",
//     color: "var(--color-error-light)",
//     link: "/collections/emergency"
//   },
//   {
//     id: 5,
//     title: "Regional Solidarity Networks",
//     description: "Trusted contacts, community groups, regional support networks for activists.",
//     count: 35,
//     updated: "Jan 2026",
//     tags: ["Community", "Regional", "Solidarity"],
//     icon: "lucide:users",
//     color: "#e9d5ff",
//     link: "/collections/regional"
//   },
//   {
//     id: 6,
//     title: "Policy & Organizational Templates",
//     description: "Customizable security policies, incident response plans, organizational safeguards.",
//     count: 22,
//     updated: "Mar 2026",
//     tags: ["Policy", "Templates", "Organization"],
//     icon: "lucide:file-text",
//     color: "#fed7aa",
//     link: "/collections/policy-templates"
//   }
// ];

// export default function ResourceCollections() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Page Header */}
//         <header className={styles.pageHeader}>
//           <div className={styles.headerTop}>
//             <Link to="/" className={styles.backLink}>
//               <Icon icon="lucide:arrow-left" />
//               Back to Home
//             </Link>
//             <h1 className={styles.pageTitle}>Resource Collections</h1>
//           </div>

//           <p className={styles.pageSubtitle}>
//             Curated bundles of safeguarding resources organized by theme, region, and use case — perfect for quick deployment.
//           </p>

//           <div className={styles.controls}>
//             <div className={styles.searchWrapper}>
//               <input
//                 type="text"
//                 placeholder="Search collections by topic or keyword..."
//                 className={styles.searchInput}
//               />
//               <Icon icon="lucide:search" className={styles.searchIcon} />
//             </div>

//             <div className={styles.filterBar}>
//               <button className={styles.filterBtnActive}>All</button>
//               <button className={styles.filterBtn}>Digital Security</button>
//               <button className={styles.filterBtn}>Legal & Advocacy</button>
//               <button className={styles.filterBtn}>Mental Health</button>
//               <button className={styles.filterBtn}>Emergency</button>
//               <button className={styles.filterBtn}>Regional</button>
//             </div>
//           </div>
//         </header>

//         {/* Featured Collection */}
//         <section className={styles.featuredSection}>
//           <div className={styles.featuredCard}>
//             <div className={styles.featuredImagePlaceholder}></div>

//             <div className={styles.featuredContent}>
//               <div className={styles.featuredTags}>
//                 {dummyFeatured.tags.map((tag, i) => (
//                   <span key={i} className={styles.tag}>{tag}</span>
//                 ))}
//               </div>

//               <h2 className={styles.featuredTitle}>{dummyFeatured.title}</h2>
//               <p className={styles.featuredDescription}>{dummyFeatured.description}</p>

//               <div className={styles.featuredMeta}>
//                 <span>{dummyFeatured.count} resources</span>
//                 <span>{dummyFeatured.rating}</span>
//               </div>

//               <Link to={dummyFeatured.link} className={styles.featuredButton}>
//                 <Icon icon="lucide:folder-open" />
//                 Open Collection
//               </Link>
//             </div>
//           </div>
//         </section>

//         {/* All Collections Grid */}
//         <section className={styles.gridSection}>
//           <h3 className={styles.gridTitle}>All Collections</h3>

//           <div className={styles.collectionsGrid}>
//             {dummyCollections.map((col) => (
//               <Link to={col.link} key={col.id} className={styles.collectionCard}>
//                 <div className={styles.cardIcon} style={{ backgroundColor: col.color }}>
//                   <Icon icon={col.icon} />
//                 </div>

//                 <h4 className={styles.cardTitle}>{col.title}</h4>
//                 <p className={styles.cardDescription}>{col.description}</p>

//                 <div className={styles.cardFooter}>
//                   <span className={styles.cardCount}>{col.count} resources</span>
//                   <span className={styles.cardUpdated}>Updated {col.updated}</span>
//                 </div>
//               </Link>
//             ))}
//           </div>
//         </section>

//         {/* Load More */}
//         <div className={styles.loadMore}>
//           <button className={styles.loadMoreBtn}>
//             Load More Collections
//             <Icon icon="lucide:arrow-down" />
//           </button>
//           <p className={styles.paginationInfo}>
//             Showing {dummyCollections.length + 1} of 47 collections
//           </p>
//         </div>
//       </div>
//     </main>
//   );
// }
// import { Icon } from '@iconify/react';
// import styles from './ResourceCollections.module.css';

// export default function ResourceCollections() {
//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Page Header */}
//         <header className={styles.pageHeader}>
//           <h1 className={styles.pageTitle}>Resource Collections</h1>
//           <p className={styles.pageSubtitle}>
//             Curated collections of safeguarding resources organized by theme, region, and use case
//           </p>

//           <div className={styles.controls}>
//             <div className={styles.searchWrapper}>
//               <input
//                 type="text"
//                 placeholder="Search collections..."
//                 className={styles.searchInput}
//               />
//               <Icon icon="lucide:search" className={styles.searchIcon} />
//             </div>

//             <div className={styles.filterBar}>
//               <button className={styles.filterBtnActive}>All</button>
//               <button className={styles.filterBtn}>Digital Security</button>
//               <button className={styles.filterBtn}>Legal & Advocacy</button>
//               <button className={styles.filterBtn}>Mental Health</button>
//               <button className={styles.filterBtn}>Emergency Protocols</button>
//             </div>
//           </div>
//         </header>

//         {/* Featured / Hero Collection */}
//         <section className={styles.featuredCollection}>
//           <div className={styles.featuredCard}>
//             <div className={styles.featuredImage}></div>
//             <div className={styles.featuredContent}>
//               <div className={styles.featuredTags}>
//                 <span className={styles.tagFeatured}>Featured</span>
//                 <span className={styles.tag}>Comprehensive</span>
//               </div>
//               <h2 className={styles.featuredTitle}>
//                 Essential Safeguarding Toolkit 2026
//               </h2>
//               <p className={styles.featuredDescription}>
//                 The most-used bundle containing digital security basics, emergency contacts, legal templates, and mental health resources — updated for current threats.
//               </p>
//               <div className={styles.featuredMeta}>
//                 <span>248 resources</span>
//                 <span>4.9 (1,204 ratings)</span>
//                 <span>Global</span>
//               </div>
//               <button className={styles.btnPrimaryLarge}>
//                 <Icon icon="lucide:folder-open" />
//                 Open Collection
//               </button>
//             </div>
//           </div>
//         </section>

//         {/* All Collections Grid */}
//         <section className={styles.collectionsGrid}>
//           <h3 className={styles.gridTitle}>All Collections</h3>

//           {/* Example collection cards – in real app you'd map from data */}
//           <div className={styles.card}>
//             <div className={styles.cardIconWrapper}>
//               <Icon icon="lucide:shield" />
//             </div>
//             <h4 className={styles.cardTitle}>Digital Security Fundamentals</h4>
//             <p className={styles.cardDescription}>
//               Device protection, secure communication, threat modeling, and best practices for high-risk users.
//             </p>
//             <div className={styles.cardMeta}>
//               <span>42 resources</span>
//               <span>Updated Feb 2026</span>
//             </div>
//             <a href="#" className={styles.cardLink}>
//               View Collection →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIconWrapper} style={{ backgroundColor: 'var(--color-success-light)' }}>
//               <Icon icon="lucide:scale" />
//             </div>
//             <h4 className={styles.cardTitle}>Legal Aid & Documentation</h4>
//             <p className={styles.cardDescription}>
//               Emergency legal contacts, affidavit templates, rights documentation, and advocacy toolkits.
//             </p>
//             <div className={styles.cardMeta}>
//               <span>31 resources</span>
//               <span>Regional filters available</span>
//             </div>
//             <a href="#" className={styles.cardLink}>
//               View Collection →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIconWrapper} style={{ backgroundColor: 'var(--color-warning-light)' }}>
//               <Icon icon="lucide:heart-pulse" />
//             </div>
//             <h4 className={styles.cardTitle}>Mental Health & Wellbeing</h4>
//             <p className={styles.cardDescription}>
//               Trauma-informed resources, peer support networks, crisis hotlines, and self-care guides.
//             </p>
//             <div className={styles.cardMeta}>
//               <span>28 resources</span>
//               <span>Multilingual</span>
//             </div>
//             <a href="#" className={styles.cardLink}>
//               View Collection →
//             </a>
//           </div>

//           <div className={styles.card}>
//             <div className={styles.cardIconWrapper} style={{ backgroundColor: 'var(--color-info-light)' }}>
//               <Icon icon="lucide:alert-triangle" />
//             </div>
//             <h4 className={styles.cardTitle}>Emergency Response Protocols</h4>
//             <p className={styles.cardDescription}>
//               Rapid response plans, evacuation checklists, incident reporting, and crisis communication templates.
//             </p>
//             <div className={styles.cardMeta}>
//               <span>19 resources</span>
//               <span>High-priority</span>
//             </div>
//             <a href="#" className={styles.cardLink}>
//               View Collection →
//             </a>
//           </div>

//           {/* You can continue adding more cards */}
//         </section>

//         {/* Load more / Pagination */}
//         <div className={styles.loadMoreWrapper}>
//           <button className={styles.loadMoreBtn}>
//             Load More Collections
//             <Icon icon="lucide:arrow-down" />
//           </button>
//           <p className={styles.loadMoreInfo}>Showing 1–12 of 47 collections</p>
//         </div>
//       </div>
//     </main>
//   );
// }